import { useQuery } from "@tanstack/react-query";
import api from "../../lib/api";
import { CircleNotch, CheckCircle, Circle, ArrowRight, Files, ListChecks, ChatCircle, Info } from "@phosphor-icons/react";

/**
 * 4-module Service Workspace — Scope, Live Status, Required Docs, Tasks & Q&A.
 * Renders for any engagement whose service_key is in the catalog.
 */
export default function ServiceWorkspaceView({ engagementId, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["service-workspace", engagementId],
    queryFn: () => api.get(`/portal/service-workspace/${engagementId}`).then((r) => r.data),
    enabled: !!engagementId,
  });

  if (isLoading) return <div className="py-10 text-center"><CircleNotch size={20} className="animate-spin mx-auto text-zinc-400" /></div>;
  if (!data) return null;

  const { engagement, catalog, workspace_stage, stages, stage_labels, documents = [], todos = [], messages = [] } = data;
  const stageIdx = stages.indexOf(workspace_stage);

  return (
    <div className="space-y-4" data-testid="service-workspace-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10.5px] uppercase tracking-wider text-zinc-400">Service workspace</p>
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Cardo', Georgia, serif" }}>{engagement.service_type}</h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">Tax year {engagement.tax_year} · Opened {new Date(engagement.opened_at || engagement.created_at || Date.now()).toLocaleDateString()}</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-[11px] text-zinc-500 hover:text-slate-900 transition-colors duration-150" data-testid="workspace-close-btn">
            ← Back to dashboard
          </button>
        )}
      </div>

      {/* Live Status Tracker */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="workspace-status-tracker">
        <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1"><ArrowRight size={11} weight="bold" /> Live progress</p>
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {stages.map((s, i) => {
            const done = i < stageIdx;
            const active = i === stageIdx;
            return (
              <div key={s} className="flex items-center gap-1 shrink-0" data-testid={`workspace-stage-${s}`}>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center border transition-colors duration-200 ${
                    done ? "bg-emerald-600 text-white border-emerald-600" :
                    active ? "bg-slate-900 text-white border-slate-900" :
                    "bg-white text-zinc-400 border-zinc-200"
                  }`}
                >
                  {done ? <CheckCircle size={13} weight="fill" /> : <Circle size={13} weight={active ? "fill" : "regular"} />}
                </div>
                <span className={`text-[10.5px] ${active ? "font-semibold text-slate-900" : done ? "text-emerald-800" : "text-zinc-500"}`}>{stage_labels[s]}</span>
                {i < stages.length - 1 && <ArrowRight size={9} className="text-zinc-300 mx-0.5" />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Scope & Details */}
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="workspace-scope">
          <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><Info size={11} weight="fill" /> Scope & deliverables</p>
          {catalog ? (
            <>
              <p className="text-[12.5px] text-zinc-700 leading-relaxed mb-2">{catalog.summary}</p>
              <ul className="space-y-1">
                {catalog.scope.map((s, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11.5px] text-slate-800" data-testid={`workspace-scope-${i}`}>
                    <CheckCircle size={10} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />{s}
                  </li>
                ))}
              </ul>
              <p className="text-[10.5px] text-zinc-400 mt-3">Timeline: {catalog.typical_timeline}</p>
            </>
          ) : (
            <p className="text-[12px] text-zinc-500">Scope details will appear once your CPA finalizes the engagement letter.</p>
          )}
        </div>

        {/* Required Docs */}
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="workspace-docs">
          <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><Files size={11} weight="fill" /> Required documents</p>
          {catalog?.required_docs?.length ? (
            <ul className="space-y-1 mb-2">
              {catalog.required_docs.map((d, i) => {
                const uploaded = documents.some((doc) => (doc.filename || "").toLowerCase().includes(d.toLowerCase().split(" ")[0]));
                return (
                  <li key={i} className="flex items-start gap-1.5 text-[11.5px]" data-testid={`workspace-doc-req-${i}`}>
                    {uploaded ? <CheckCircle size={10} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> : <Circle size={10} className="text-zinc-300 mt-0.5 shrink-0" />}
                    <span className={uploaded ? "text-zinc-500 line-through" : "text-slate-800"}>{d}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <p className="text-[10.5px] text-zinc-400">
            {documents.length ? `${documents.length} file${documents.length === 1 ? "" : "s"} uploaded so far` : "No files uploaded yet"} · use the Documents & Vault tab to add more.
          </p>
        </div>

        {/* Pending Tasks */}
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="workspace-tasks">
          <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><ListChecks size={11} weight="fill" /> Pending tasks</p>
          {todos.length === 0 ? (
            <p className="text-[11.5px] text-zinc-400 italic">No pending tasks assigned yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {todos.filter((t) => !t.done).slice(0, 6).map((t, i) => (
                <li key={t.id} className="flex items-start gap-1.5 text-[12px]" data-testid={`workspace-todo-${i}`}>
                  <Circle size={11} className="text-amber-500 mt-0.5 shrink-0" />
                  <span className="text-slate-800">{t.title || t.body || t.description}</span>
                </li>
              ))}
              {todos.filter((t) => t.done).length > 0 && (
                <li className="text-[10.5px] text-emerald-700 pt-1">✓ {todos.filter((t) => t.done).length} completed</li>
              )}
            </ul>
          )}
        </div>

        {/* CPA Q&A thread */}
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="workspace-qa">
          <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><ChatCircle size={11} weight="fill" /> CPA questions & updates</p>
          {messages.length === 0 ? (
            <p className="text-[11.5px] text-zinc-400 italic">No messages on this workspace yet.</p>
          ) : (
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {messages.slice(-5).map((m) => (
                <li key={m.id} className={`rounded-sm px-2 py-1.5 border text-[11.5px] ${m.sender_role === "firm" ? "border-indigo-200 bg-indigo-50/50" : "border-emerald-200 bg-emerald-50/50"}`} data-testid={`workspace-msg-${m.id}`}>
                  <p className="text-[10px] text-zinc-500 mb-0.5">{m.sender_name} · {new Date(m.created_at).toLocaleDateString()}</p>
                  <p className="text-slate-800 whitespace-pre-line">{m.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
